export class TypingArea {
    constructor(containerId) {
        this.words = [];
        this.activeWordIndex = 0;
        this.activeLetterIndex = 0;
        this.lineElements = [];
        this.fontSize = 'medium';
        this.blindMode = false;
        this.mode = '';
        // Simplified 3-line rendering system
        this.allLines = []; // All lines (full word array organized)
        this.visibleLineIndices = []; // Indices of currently visible lines (always 3: [top, middle, bottom])
        this.activeLineIndex = 0; // Which actual line index (in allLines) is active
        const container = document.getElementById(containerId);
        if (!container) {
            throw new Error(`Container with id "${containerId}" not found`);
        }
        this.container = container;
    }
    setMode(mode) {
        this.mode = mode;
        // Remove all mode classes
        this.container.classList.remove('paragraphs-mode', 'words-mode', 'time-mode', 'quote-mode', 'zen-mode');
        // Set data attribute and class for CSS targeting
        this.container.setAttribute('data-mode', mode);
        if (mode) {
            this.container.classList.add(`${mode}-mode`);
        }
    }
    setFontSize(size) {
        this.fontSize = size;
        // Remove all font size classes
        this.container.classList.remove('typeracer-font-small', 'typeracer-font-medium', 'typeracer-font-large', 'typeracer-font-xlarge');
        // Set data attribute and class for CSS targeting
        this.container.setAttribute('data-font-size', size);
        this.container.classList.add(`typeracer-font-${size}`);
    }
    setBlindMode(enabled) {
        this.blindMode = enabled;
        this.updateBlindMode();
    }
    updateBlindMode() {
        if (!this.blindMode) {
            // Show all letters
            this.container.querySelectorAll('[data-letter-id]').forEach(el => {
                el.style.opacity = '1';
            });
            return;
        }
        // Hide letters that haven't been typed yet
        this.words.forEach((word, wordIdx) => {
            word.letters.forEach((letter, letterIdx) => {
                const letterElement = this.container.querySelector(`[data-letter-id="${letter.id}"]`);
                if (letterElement) {
                    if (wordIdx < this.activeWordIndex) {
                        // Past words - show all
                        letterElement.style.opacity = '1';
                    }
                    else if (wordIdx === this.activeWordIndex) {
                        // Current word - show only typed letters
                        if (letterIdx < this.activeLetterIndex) {
                            letterElement.style.opacity = '1';
                        }
                        else {
                            letterElement.style.opacity = '0';
                        }
                    }
                    else {
                        // Future words - hide all
                        letterElement.style.opacity = '0';
                    }
                }
            });
        });
    }
    renderWords(words) {
        this.words = words;
        // Save the caret element before clearing (it will be removed by innerHTML = '')
        const caretElement = this.container.querySelector('.smooth-caret');
        this.container.innerHTML = '';
        this.lineElements = [];
        // Apply font size first so measurements are accurate
        this.setFontSize(this.fontSize);
        // Wait for font size to be applied, then organize words into lines
        requestAnimationFrame(() => {
            // Organize words into lines based on container width
            this.allLines = this.organizeWordsIntoLines(words);
            // Find initial active line based on word index
            this.activeLineIndex = this.findLineIndexForWord(0);
            if (this.activeLineIndex === -1) {
                this.activeLineIndex = 0;
            }
            // Initialize with first 3 lines, keeping active line in middle
            this.initializeVisibleLines();
            // Render the 3 visible lines
            this.renderVisibleLines();
            // Restore the caret element AFTER rendering words so it stays on top
            if (caretElement) {
                this.container.appendChild(caretElement);
            }
            // Use double requestAnimationFrame to ensure DOM is fully rendered and layout is calculated
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.updateActiveWord();
                    if (this.blindMode) {
                        this.updateBlindMode();
                    }
                    // Also trigger a caret position update event to ensure caret knows about the elements
                    this.updateCaretPosition();
                });
            });
        });
    }
    /**
     * Render only the 3 visible lines
     */
    renderVisibleLines() {
        // Clear existing line elements
        this.lineElements.forEach(el => el.remove());
        this.lineElements = [];
        // Render the 3 visible lines
        this.visibleLineIndices.forEach((lineIndex, visibleIndex) => {
            if (lineIndex < this.allLines.length) {
                const lineWords = this.allLines[lineIndex];
                const lineElement = document.createElement('div');
                lineElement.className = 'typing-line';
                lineElement.setAttribute('data-line-index', lineIndex.toString());
                lineElement.setAttribute('data-visible-index', visibleIndex.toString());
                lineWords.forEach(word => {
                    const wordElement = this.createWordElement(word);
                    lineElement.appendChild(wordElement);
                });
                this.container.appendChild(lineElement);
                this.lineElements.push(lineElement);
            }
        });
    }
    setActiveWord(wordIndex, letterIndex) {
        this.activeWordIndex = wordIndex;
        this.activeLetterIndex = letterIndex;
        this.updateActiveWord();
        if (this.blindMode) {
            this.updateBlindMode();
        }
    }
    updateActiveWord() {
        // Keep the active typing line centered before touching DOM letter refs
        this.detectAndHandleLineChange();
        // Remove active class from all words
        this.container.querySelectorAll('[data-word-id]').forEach(word => {
            word.classList.remove('bg-yellow-400/20', 'active');
        });
        // Add active class to current word
        const activeWordElement = this.container.querySelector(`[data-word-id="word-${this.activeWordIndex}"]`);
        if (activeWordElement) {
            activeWordElement.classList.add('bg-yellow-400/20', 'active');
        }
        // Update letter states
        this.updateLetterStates();
        this.updateCaretPosition();
    }
    updateLetterStates() {
        const activeWord = this.words[this.activeWordIndex];
        if (!activeWord)
            return;
        const stateClasses = {
            'neutral': 'text-gray-500',
            'correct': 'text-gray-300',
            'incorrect': 'text-red-400 underline',
            'extra': 'text-red-600 dark:text-red-700 bg-red-900/20' // Darker red for extra letters as per description
        };
        // Find the word element in the DOM
        const wordElement = this.container.querySelector(`[data-word-id="${activeWord.id}"]`);
        if (!wordElement)
            return;
        activeWord.letters.forEach((letter, index) => {
            let letterElement = this.container.querySelector(`[data-letter-id="${letter.id}"]`);
            // If letter element doesn't exist, create it
            if (!letterElement) {
                letterElement = document.createElement('span');
                letterElement.className = `letter transition-colors duration-100 ${stateClasses[letter.state] || stateClasses.neutral}`;
                letterElement.setAttribute('data-letter-id', letter.id);
                letterElement.textContent = letter.char === ' ' ? '\u00A0' : letter.char;
                letter.elementRef = letterElement;
                // Find where to insert it - before the space element at the end of the word
                const spaceElement = wordElement.querySelector('span:last-child');
                if (spaceElement && spaceElement.textContent === '\u00A0' && !spaceElement.hasAttribute('data-letter-id')) {
                    // Insert before the space element
                    wordElement.insertBefore(letterElement, spaceElement);
                }
                else {
                    // Fallback: append to word element
                    wordElement.appendChild(letterElement);
                }
            }
            else {
                // Update existing element
                letterElement.className = `letter transition-colors duration-100 ${stateClasses[letter.state] || stateClasses.neutral}`;
                letter.elementRef = letterElement;
            }
        });
    }
    updateCaretPosition() {
        const activeWord = this.words[this.activeWordIndex];
        if (!activeWord)
            return;
        // Determine which element to use for cursor positioning
        let targetElement = null;
        let positionAfter = false;
        // If we've typed at least one letter, position after the last typed letter
        if (this.activeLetterIndex > 0) {
            const previousLetter = activeWord.letters[this.activeLetterIndex - 1];
            if (previousLetter?.elementRef) {
                targetElement = previousLetter.elementRef;
                positionAfter = true;
            }
        }
        // Fallback to current letter if no previous letter (at start of word)
        if (!targetElement) {
            const activeLetter = activeWord.letters[this.activeLetterIndex];
            if (activeLetter?.elementRef) {
                targetElement = activeLetter.elementRef;
                positionAfter = false; // Position at the start of the letter
            }
        }
        // If still no element, try to find it in the DOM
        if (!targetElement) {
            const activeLetter = activeWord.letters[this.activeLetterIndex];
            if (activeLetter) {
                const letterElement = this.container.querySelector(`[data-letter-id="${activeLetter.id}"]`);
                if (letterElement) {
                    targetElement = letterElement;
                    activeLetter.elementRef = letterElement;
                    positionAfter = false;
                }
            }
        }
        if (!targetElement) {
            // No element found, can't position caret
            return;
        }
        // Emit event for caret component with positioning hint
        const event = new CustomEvent('caret-position-update', {
            detail: {
                element: targetElement,
                positionAfter: positionAfter
            }
        });
        document.dispatchEvent(event);
    }
    /**
     * Compute the 3-line window so the active line stays in the middle whenever possible.
     * Start: [0, 1, 2] with caret on top until the user reaches line 1.
     * Middle: [active-1, active, active+1].
     * End: last 3 lines with caret on the bottom line only when on the final line.
     */
    initializeVisibleLines() {
        this.visibleLineIndices = [];
        if (this.allLines.length === 0) {
            return;
        }
        const last = this.allLines.length - 1;
        const active = Math.max(0, Math.min(this.activeLineIndex, last));
        let topIndex;
        let middleIndex;
        let bottomIndex;
        if (active <= 0) {
            topIndex = 0;
            middleIndex = Math.min(1, last);
            bottomIndex = Math.min(2, last);
        }
        else if (active >= last) {
            topIndex = Math.max(0, last - 2);
            middleIndex = Math.max(0, last - 1);
            bottomIndex = last;
        }
        else {
            topIndex = active - 1;
            middleIndex = active;
            bottomIndex = active + 1;
        }
        const indices = [topIndex, middleIndex, bottomIndex]
            .filter((value, index, arr) => arr.indexOf(value) === index)
            .sort((a, b) => a - b);
        while (indices.length < 3 && indices[indices.length - 1] < last) {
            indices.push(indices[indices.length - 1] + 1);
        }
        while (indices.length < 3 && indices[0] > 0) {
            indices.unshift(indices[0] - 1);
        }
        this.visibleLineIndices = indices.slice(0, 3);
    }
    /**
     * Whether the current visible window already keeps the active line centered
     * (or in the only valid edge position at start/end).
     */
    isActiveLineCentered() {
        if (this.visibleLineIndices.length === 0 || this.allLines.length === 0) {
            return false;
        }
        const active = this.activeLineIndex;
        const last = this.allLines.length - 1;
        const top = this.visibleLineIndices[0];
        const middle = this.visibleLineIndices[1] ?? top;
        const bottom = this.visibleLineIndices[this.visibleLineIndices.length - 1];
        if (active <= 0) {
            return top === 0 && this.visibleLineIndices.includes(0);
        }
        if (active >= last) {
            return bottom === last;
        }
        return middle === active && top === active - 1 && bottom === active + 1;
    }
    /**
     * Detect line changes from the active word index and keep the typing line centered.
     */
    detectAndHandleLineChange() {
        const newActiveLineIndex = this.findLineIndexForWord(this.activeWordIndex);
        if (newActiveLineIndex === -1) {
            return;
        }
        const lineChanged = newActiveLineIndex !== this.activeLineIndex;
        this.activeLineIndex = newActiveLineIndex;
        if (lineChanged || !this.isActiveLineCentered()) {
            this.recenterVisibleLines();
        }
    }
    /**
     * Find which line index contains the given word index
     */
    findLineIndexForWord(wordIndex) {
        if (wordIndex < 0 || wordIndex >= this.words.length) {
            return -1;
        }
        let wordCount = 0;
        for (let lineIndex = 0; lineIndex < this.allLines.length; lineIndex++) {
            const line = this.allLines[lineIndex];
            if (wordIndex < wordCount + line.length) {
                return lineIndex;
            }
            wordCount += line.length;
        }
        return this.allLines.length - 1;
    }
    /**
     * Rebuild the 3 visible lines so the active typing line stays in the middle.
     * Synchronous re-render avoids the previous async swap corrupting DOM order.
     */
    recenterVisibleLines() {
        const previousVisible = [...this.visibleLineIndices];
        this.initializeVisibleLines();
        const unchanged = previousVisible.length === this.visibleLineIndices.length &&
            previousVisible.every((value, index) => value === this.visibleLineIndices[index]);
        if (unchanged) {
            return;
        }
        const caretElement = this.container.querySelector('.smooth-caret');
        this.renderVisibleLines();
        if (caretElement) {
            this.container.appendChild(caretElement);
        }
        // Only three lines are rendered, so keep the viewport pinned to the top
        this.container.scrollTop = 0;
    }
    scrollToCaret(element, positionAfter) {
        if (!this.container)
            return;
        // Use requestAnimationFrame to ensure DOM is ready and measurements are accurate
        requestAnimationFrame(() => {
            // Get the position of the element relative to the container
            const elementRect = element.getBoundingClientRect();
            const containerRect = this.container.getBoundingClientRect();
            // Calculate the caret position relative to the container
            const caretX = elementRect.left - containerRect.left + (positionAfter ? elementRect.width : 0);
            const caretY = elementRect.top - containerRect.top;
            // Get container scroll position and dimensions
            const scrollTop = this.container.scrollTop;
            const scrollLeft = this.container.scrollLeft;
            const containerHeight = this.container.clientHeight;
            const containerWidth = this.container.clientWidth;
            // Calculate the absolute position of the caret within the scrollable content
            const absoluteCaretY = caretY + scrollTop;
            const absoluteCaretX = caretX + scrollLeft;
            // Get line height for calculating next line position
            const computedStyle = getComputedStyle(this.container);
            const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.8;
            // Calculate the position where we want the caret to be (slightly above center)
            // This ensures the next line is visible below
            const targetCaretPosition = containerHeight * 0.4; // 40% from top
            const nextLineY = absoluteCaretY + lineHeight;
            // Determine if we need to scroll
            let newScrollTop = scrollTop;
            let newScrollLeft = scrollLeft;
            // Calculate the visible area bounds
            const visibleTop = scrollTop;
            const visibleBottom = scrollTop + containerHeight;
            const visibleLeft = scrollLeft;
            const visibleRight = scrollLeft + containerWidth;
            // Vertical scrolling: position caret at target position and ensure next line is visible
            const bottomPadding = 60; // Minimum space at bottom for next line
            // If caret is above the target position, scroll up
            if (absoluteCaretY < visibleTop + targetCaretPosition) {
                newScrollTop = absoluteCaretY - targetCaretPosition;
            }
            // If caret is below target position or next line would be cut off, scroll down
            else if (absoluteCaretY > visibleTop + targetCaretPosition || nextLineY > visibleBottom - bottomPadding) {
                // Scroll to show caret at target position, ensuring next line is visible
                newScrollTop = absoluteCaretY - targetCaretPosition;
                // If next line would still be cut off, adjust to show it
                if (nextLineY - newScrollTop > visibleBottom - bottomPadding) {
                    newScrollTop = nextLineY - containerHeight + bottomPadding;
                }
            }
            // Horizontal scrolling: prevent horizontal scrolling (should never happen, but just in case)
            const horizontalPadding = 20;
            if (absoluteCaretX < visibleLeft + horizontalPadding) {
                newScrollLeft = absoluteCaretX - horizontalPadding;
            }
            else if (absoluteCaretX > visibleRight - horizontalPadding) {
                newScrollLeft = absoluteCaretX - containerWidth + horizontalPadding;
            }
            // Apply smooth scrolling to container if position changed
            if (Math.abs(newScrollTop - scrollTop) > 1 || Math.abs(newScrollLeft - scrollLeft) > 1) {
                this.container.scrollTo({
                    top: Math.max(0, newScrollTop),
                    left: Math.max(0, newScrollLeft),
                    behavior: 'smooth'
                });
            }
        });
    }
    createWordElement(word) {
        const wordElement = document.createElement('span');
        wordElement.className = 'word inline-block mr-1 transition-colors duration-150';
        wordElement.setAttribute('data-word-id', word.id);
        // Ensure word doesn't cause horizontal overflow
        wordElement.style.maxWidth = '100%';
        word.letters.forEach(letter => {
            const letterElement = document.createElement('span');
            const stateClasses = {
                'neutral': 'text-gray-500',
                'correct': 'text-gray-300',
                'incorrect': 'text-red-400 underline',
                'extra': 'text-red-600 dark:text-red-700 bg-red-900/20' // Darker red for extra letters as per description
            };
            letterElement.className = `letter transition-colors duration-100 ${stateClasses[letter.state] || stateClasses.neutral}`;
            letterElement.setAttribute('data-letter-id', letter.id);
            letterElement.textContent = letter.char === ' ' ? '\u00A0' : letter.char;
            letter.elementRef = letterElement;
            wordElement.appendChild(letterElement);
        });
        // Add space after word
        const spaceElement = document.createElement('span');
        spaceElement.className = 'inline';
        spaceElement.textContent = '\u00A0';
        wordElement.appendChild(spaceElement);
        return wordElement;
    }
    organizeWordsIntoLines(words) {
        const lines = [];
        let currentLine = [];
        // Get container width - use clientWidth to exclude scrollbar
        const containerWidth = this.container.clientWidth || this.container.offsetWidth;
        // Get computed styles to match exactly
        const computedStyle = getComputedStyle(this.container);
        const containerPadding = parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight);
        const availableWidth = containerWidth - containerPadding;
        // Create a temporary container to measure word widths with exact same styling
        const measureContainer = document.createElement('div');
        measureContainer.style.position = 'absolute';
        measureContainer.style.visibility = 'hidden';
        measureContainer.style.whiteSpace = 'nowrap';
        measureContainer.style.top = '-9999px';
        measureContainer.style.left = '-9999px';
        // Copy all relevant styles from the container
        measureContainer.style.fontFamily = computedStyle.fontFamily;
        measureContainer.style.fontSize = computedStyle.fontSize;
        measureContainer.style.fontWeight = computedStyle.fontWeight;
        measureContainer.style.letterSpacing = computedStyle.letterSpacing;
        measureContainer.style.lineHeight = computedStyle.lineHeight;
        // Also apply font size class to match
        measureContainer.className = this.container.className;
        document.body.appendChild(measureContainer);
        let currentLineWidth = 0;
        words.forEach((word) => {
            // Create a temporary word element to measure its width
            const tempWordElement = this.createWordElement(word);
            measureContainer.appendChild(tempWordElement);
            // Force a reflow to ensure accurate measurement
            void measureContainer.offsetWidth;
            const wordWidth = tempWordElement.offsetWidth;
            measureContainer.removeChild(tempWordElement);
            // Check if adding this word would exceed the line width
            // Add margin for spacing between words (mr-1 class = 0.25rem = 4px typically)
            const wordWidthWithMargin = wordWidth + 4;
            // If this is the first word, always add it (even if it's wider than the container)
            if (currentLine.length === 0) {
                currentLine.push(word);
                currentLineWidth = wordWidth;
            }
            else if (currentLineWidth + wordWidthWithMargin > availableWidth) {
                // Start a new line - current word doesn't fit
                lines.push([...currentLine]);
                currentLine = [word];
                currentLineWidth = wordWidth;
            }
            else {
                // Add word to current line
                currentLine.push(word);
                currentLineWidth += wordWidthWithMargin;
            }
        });
        // Add the last line if it has words
        if (currentLine.length > 0) {
            lines.push(currentLine);
        }
        // Clean up measurement container
        document.body.removeChild(measureContainer);
        return lines;
    }
    getActiveLetterElement() {
        const activeWord = this.words[this.activeWordIndex];
        if (!activeWord)
            return null;
        const activeLetter = activeWord.letters[this.activeLetterIndex];
        return activeLetter?.elementRef || null;
    }
    togglePause(paused) {
        if (paused) {
            this.container.classList.add('paused');
            // Add paused overlay
            const overlay = document.createElement('div');
            overlay.className = 'pause-overlay absolute inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center';
            overlay.innerHTML = `
          <div class="text-center animate-fade-in-up">
              <div class="text-4xl mb-4">⏸️</div>
              <h2 class="text-2xl font-bold text-white mb-2">Pausitud</h2>
              <p class="text-gray-300">Jätkamiseks vajuta ESC</p>
          </div>
      `;
            // Append to typing area container's parent which should be relative
            this.container.parentElement?.appendChild(overlay);
        }
        else {
            this.container.classList.remove('paused');
            // Remove paused overlay
            const overlay = this.container.parentElement?.querySelector('.pause-overlay');
            if (overlay)
                overlay.remove();
        }
    }
}
